/**
 * utilities/pushBroadcast.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The shared vocabulary for an admin push broadcast to the Lusuku mobile apps
 * (POST /expoPushTokens/message) plus the operator Web Push broadcast
 * (POST /webpush/broadcast).
 *
 * Three things have to agree and only one of them is React: the choices an
 * operator sees, the body the API expects, and how a send result is read back.
 * Keeping them in one dependency-free module is why a send can no longer claim
 * success while the API reported none. This was lifted out of the unmounted
 * views/Forms/NotificationsForm.js so there is exactly one copy of it; the
 * Notifications centre "Send push" tab is the only caller.
 *
 * Server contract (routes/expoPushTokensRoute.js):
 *   body    { title, message, audience, userId?, country?, data?, respectPrefs,
 *             mode, sendAt? }
 *   200     { success, data: { audience, customersSent, ridersSent, totalSent,
 *                              customersTargeted, ridersTargeted,
 *                              respectedPrefs }, error? }
 *   success is FALSE when nothing was sent, so it must be read, not assumed.
 * ───────────────────────────────────────────────────────────────────────────── */

import { MARKETS } from "../config/markets.js";

/** Sentinel for "every market", i.e. send no country filter at all. */
export const ALL_MARKETS = "all";

/** Sentinel deep link that links to one product instead of a named screen. */
export const PRODUCT_DEEP_LINK = "__product__";

// Audience is enforced server side too; this list only drives the UI.
export const PUSH_AUDIENCES = Object.freeze([
  { value: "customers", label: "Customers only" },
  { value: "riders", label: "Riders only" },
  { value: "all", label: "Both (customers and riders)" },
  { value: "user", label: "One customer (by user ID)" },
]);

const AUDIENCE_VALUES = PUSH_AUDIENCES.map((a) => a.value);

// Optional deep link. These match the `screen` values the apps route on when a
// notification is tapped. Blank just opens the app.
export const PUSH_DEEP_LINKS = Object.freeze([
  { value: "", label: "Just open the app" },
  { value: "Deals", label: "Deals" },
  { value: "MyTickets", label: "My Tickets" },
  { value: "TicketingEvents", label: "Events and Tickets" },
  { value: "My Orders", label: "Order History" },
  { value: PRODUCT_DEEP_LINK, label: "A specific product..." },
]);

// Which collection the product id belongs to. The app resolves the product
// through GET /api/catalog/items/:source/:productId and opens the right detail
// screen for that source.
export const PUSH_PRODUCT_SOURCES = Object.freeze([
  { value: "retail", label: "Retailer / Wholesaler" },
  { value: "inhouse", label: "In-house (Lusuku)" },
  { value: "brand", label: "Brand" },
  { value: "restaurant", label: "Restaurant" },
  { value: "healthcare", label: "Healthcare" },
]);

/**
 * Market choices for the audience picker, driven by the market registry so a
 * new market is an entry in config/markets.js and nothing else.
 * @returns {{value: string, label: string}[]}
 */
export function pushMarketOptions() {
  return [
    ...Object.values(MARKETS).map((m) => ({
      value: m.country,
      label: `${m.name} (${m.country}) only`,
    })),
    { value: ALL_MARKETS, label: "Every market" },
  ];
}

/**
 * Why a form cannot be sent yet, in operator language.
 * @param {object} form
 * @param {{ mobile?: boolean }} [targets] which destinations are ticked
 * @param {number} [now] injectable clock, for tests
 * @returns {string|null} null when the form is ready to send
 */
export function validatePushBroadcast(form = {}, targets = {}, now = Date.now()) {
  const title = String(form.title || "").trim();
  const message = String(form.message || "").trim();
  if (!title || !message) return "Title and message are required.";
  if (targets.mobile === false) return null;
  if (form.audience === "user" && !String(form.userId || "").trim()) {
    return "Enter the user ID to send to a single customer.";
  }
  if (form.screen === PRODUCT_DEEP_LINK && !String(form.productId || "").trim()) {
    return "Enter the product ID to link this notification to a product.";
  }
  // A bad send time is caught here, before the request, in the server's words.
  return validatePushSchedule(form, now);
}

/**
 * Build the POST /expoPushTokens/message body from the form state. Every field
 * the operator chose is carried through: an omitted audience would silently
 * become "all" on the server, and an omitted country would reach every market.
 * A scheduled send adds mode + sendAt, the local pick converted to UTC.
 * @param {object} form
 * @returns {object}
 */
export function buildPushBroadcastBody(form = {}) {
  const audience = AUDIENCE_VALUES.includes(form.audience) ? form.audience : "all";
  const country = String(form.country || "").trim().toUpperCase();
  const screen = String(form.screen || "");
  const body = {
    title: String(form.title || "").trim(),
    message: String(form.message || "").trim(),
    audience,
    // A marketing push honours the customer's Offers switch. Only a service
    // message (an outage, a policy change) sets respectPrefs:false, and the
    // operator has to tick that box deliberately.
    respectPrefs: form.serviceMessage !== true,
  };
  if (audience === "user") body.userId = String(form.userId || "").trim();
  if (country && country !== ALL_MARKETS.toUpperCase()) body.country = country;
  if (screen === PRODUCT_DEEP_LINK) {
    // A product link sends productId + source instead of a screen name; the app
    // resolves the product and picks the right detail screen.
    body.data = {
      productId: String(form.productId || "").trim(),
      source: form.source || PUSH_PRODUCT_SOURCES[0].value,
    };
  } else if (screen) {
    body.data = { screen };
  }
  // Timing. "now" is the server default too, but it is sent explicitly so the
  // campaign row records what the operator actually chose.
  const mode = form.mode === "scheduled" ? "scheduled" : "now";
  body.mode = mode;
  if (mode === "scheduled") body.sendAt = localDateTimeToIso(form.sendAtLocal);
  return body;
}

/**
 * Read a mobile broadcast response honestly. `ok` is true only when the API
 * said success AND at least one device was actually sent to; the targeted
 * counts are reported because the gap between targeted and sent is dead tokens,
 * and that gap is what tells ops whether a campaign really went out.
 * @param {object} payload the axios response body
 * @returns {{ ok: boolean, text: string, totalSent: number, targeted: number }}
 */
export function summarisePushBroadcast(payload) {
  const d = (payload && payload.data) || {};
  const totalSent = Number(d.totalSent || 0);
  const customersSent = Number(d.customersSent || 0);
  const ridersSent = Number(d.ridersSent || 0);
  const targeted =
    Number(d.customersTargeted || 0) + Number(d.ridersTargeted || 0);
  const ok = Boolean(payload && payload.success) && totalSent > 0;
  if (!ok) {
    return {
      ok: false,
      totalSent,
      targeted,
      text:
        (payload && payload.error) ||
        (targeted > 0
          ? `nothing was delivered to the ${targeted} device(s) targeted.`
          : "no recipient matched this audience, so nothing was sent."),
    };
  }
  const audiencePart =
    d.audience === "user"
      ? ""
      : ` (customers ${customersSent}, riders ${ridersSent})`;
  const prefsPart = d.respectedPrefs
    ? ""
    : " Sent as a service message, so customers who turned Offers off were included.";
  return {
    ok: true,
    totalSent,
    targeted,
    text: `sent to ${totalSent} of ${targeted} registered device(s)${audiencePart}.${prefsPart}`,
  };
}

/**
 * The same honesty for the operator Web Push broadcast, which answers
 * { success, recipients, delivered, failed }.
 * @param {object} payload the axios response body
 * @returns {{ ok: boolean, text: string }}
 */
export function summariseWebPushBroadcast(payload) {
  const recipients = Number(payload?.recipients || 0);
  const delivered = Number(payload?.delivered || 0);
  const failed = Number(payload?.failed || 0);
  if (!payload?.success) {
    return { ok: false, text: payload?.error || "the broadcast was refused." };
  }
  if (delivered === 0) {
    return {
      ok: false,
      text:
        recipients > 0
          ? `none of the ${recipients} subscribed browser(s) accepted it.`
          : "no browser in this market is subscribed for Web Push.",
    };
  }
  const failedPart = failed > 0 ? ` ${failed} failed.` : "";
  return {
    ok: true,
    text: `delivered to ${delivered} of ${recipients} subscribed browser(s).${failedPart}`,
  };
}

/* ── Timing ───────────────────────────────────────────────────────────────────
 * A send is either immediate or parked for the scheduler. The operator picks a
 * time on their own clock (Kampala, for the staff who do this); the wire format
 * is always UTC, so the conversion happens here, once, and every confirmation
 * is rendered back in local time so there is never a question of which clock
 * was meant. The two limits mirror services/pushCampaignService.js: a send time
 * more than a minute in the past is refused with the same words the server
 * uses, so the operator never sees two different sentences for one mistake.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Clock skew the server forgives before it calls a time "in the past". */
export const IMMEDIATE_WINDOW_MS = 60 * 1000;
export const MAX_SCHEDULE_AHEAD_MS = 365 * 24 * 60 * 60 * 1000;

export const SCHEDULE_PAST_ERROR = "That send time is in the past";
export const SCHEDULE_TOO_FAR_ERROR =
  "A campaign cannot be scheduled more than a year ahead";
export const SCHEDULE_INVALID_ERROR =
  "A valid send time is required to schedule";

export const PUSH_TIMING = Object.freeze([
  { value: "now", label: "Send now" },
  { value: "scheduled", label: "Schedule for later" },
]);

/**
 * Parse the value of an `<input type="datetime-local">` as LOCAL time. Built
 * field by field through the Date constructor rather than by parsing the
 * string, because that constructor is local by definition and leaves nothing
 * to a browser's interpretation.
 * @param {string} value "YYYY-MM-DDTHH:mm"
 * @returns {Date|null}
 */
export function localDateTimeToDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(value || ""));
  if (!m) return null;
  const d = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    0,
    0
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The same value as the UTC ISO string the API stores.
 * @param {string} value "YYYY-MM-DDTHH:mm"
 * @returns {string|null}
 */
export function localDateTimeToIso(value) {
  const d = localDateTimeToDate(value);
  return d ? d.toISOString() : null;
}

/**
 * A Date as an `<input type="datetime-local">` value, in local time. Used for
 * the field's `min` and for pre-filling a sensible first guess.
 * @param {Date|string|number} date
 * @returns {string}
 */
export function toLocalDateTimeInput(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** The IANA zone this browser is in, e.g. "Africa/Kampala". */
export function localTimeZoneLabel() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "your device time zone";
  } catch (err) {
    return "your device time zone";
  }
}

/**
 * A UTC instant rendered in the reader's own time zone.
 * @param {Date|string|number} value
 * @returns {string} "" when there is nothing to show
 */
export function formatLocalDateTime(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The same, with the zone named, for a confirmation that must be unambiguous. */
export function formatLocalDateTimeWithZone(value) {
  const text = formatLocalDateTime(value);
  return text ? `${text} (${localTimeZoneLabel()})` : "";
}

/**
 * Why a schedule cannot be accepted, in the server's own words so the client
 * and the API never disagree about the same mistake.
 * @param {object} form
 * @param {number} [now]
 * @returns {string|null}
 */
export function validatePushSchedule(form = {}, now = Date.now()) {
  if (form.mode !== "scheduled") return null;
  const when = localDateTimeToDate(form.sendAtLocal);
  if (!when) return SCHEDULE_INVALID_ERROR;
  if (when.getTime() < now - IMMEDIATE_WINDOW_MS) return SCHEDULE_PAST_ERROR;
  if (when.getTime() > now + MAX_SCHEDULE_AHEAD_MS) return SCHEDULE_TOO_FAR_ERROR;
  return null;
}

/* ── Reading a send back ──────────────────────────────────────────────────── */

/**
 * The single reader for POST /expoPushTokens/message, which has three answers:
 * a scheduled campaign, a finished send with counts, and a 202 for a send still
 * running when the HTTP request had to return. Only the middle one carries
 * numbers, so the other two say plainly what happened instead of borrowing the
 * language of a completed send.
 * @param {{status?: number, data?: object}} response the whole axios response
 * @returns {{ ok: boolean, pending: boolean, scheduled: boolean, text: string }}
 */
export function summarisePushSend(response) {
  const status = Number(response?.status || 200);
  const payload = response?.data || {};

  if (payload.scheduled) {
    const when = formatLocalDateTimeWithZone(payload?.data?.sendAt);
    return {
      ok: true,
      pending: true,
      scheduled: true,
      text: when
        ? `scheduled. It goes out on ${when}.`
        : "scheduled. It goes out at the time you picked.",
    };
  }

  if (status === 202 || payload.running) {
    return {
      ok: true,
      pending: true,
      scheduled: false,
      text:
        "the send is still running. The final numbers appear in the list below, so refresh it in a moment.",
    };
  }

  return { ...summarisePushBroadcast(payload), pending: false, scheduled: false };
}

/* ── The campaign list ────────────────────────────────────────────────────── */

// Status to operator language plus the badge colour. "completed" is called
// "Sent" because that is the question being asked of the row.
export const PUSH_CAMPAIGN_STATUS = Object.freeze({
  scheduled: { label: "Scheduled", variant: "info" },
  sending: { label: "Sending", variant: "warning" },
  completed: { label: "Sent", variant: "success" },
  cancelled: { label: "Cancelled", variant: "secondary" },
  failed: { label: "Failed", variant: "danger" },
});

/**
 * @param {string} status
 * @returns {{ label: string, variant: string }}
 */
export function pushCampaignStatus(status) {
  return (
    PUSH_CAMPAIGN_STATUS[String(status || "")] || {
      label: String(status || "Unknown"),
      variant: "secondary",
    }
  );
}

/** Only a campaign that has not started can be called back. */
export function isCancellableCampaign(campaign) {
  return campaign?.status === "scheduled";
}

/** The audience in the same words the form offered. */
export function campaignAudienceLabel(campaign) {
  const type = campaign?.audience?.type;
  const found = PUSH_AUDIENCES.find((a) => a.value === type);
  return found ? found.label : type || "Unknown";
}

/** The market, or the fact that there was no market filter at all. */
export function campaignMarketLabel(campaign) {
  const country = campaign?.audience?.country;
  return country ? String(country).toUpperCase() : "Every market";
}

/**
 * The one time that matters for this row, in local time: what it is waiting
 * for, or what already happened.
 * @param {object} campaign
 * @returns {{ label: string, when: string }}
 */
export function campaignWhen(campaign) {
  const status = campaign?.status;
  const labels = {
    scheduled: "Goes out",
    sending: "Started",
    completed: "Sent",
    cancelled: "Cancelled",
    failed: "Failed",
  };
  const value =
    status === "scheduled"
      ? campaign?.schedule?.sendAt
      : campaign?.completedAt ||
        campaign?.cancelledAt ||
        campaign?.launchedAt ||
        campaign?.schedule?.sendAt;
  return { label: labels[status] || "When", when: formatLocalDateTime(value) };
}

/**
 * Devices Expo accepted over devices targeted. A scheduled row has no numbers
 * yet and says so rather than showing a misleading 0 / 0.
 * @param {object} campaign
 * @returns {{ sent: number, targeted: number, text: string }}
 */
export function campaignCounts(campaign) {
  const s = campaign?.stats || {};
  const sent = Number(s.customersSent || 0) + Number(s.ridersSent || 0);
  const targeted = Number(s.customersTargeted || 0) + Number(s.ridersTargeted || 0);
  return {
    sent,
    targeted,
    text: campaign?.status === "scheduled" ? "not sent yet" : `${sent} / ${targeted}`,
  };
}

/** Receipts should be in by now; after this, "awaiting" becomes a warning. */
const RECEIPT_GRACE_MS = 30 * 60 * 1000;

/**
 * What the delivery RECEIPTS said about a campaign, for the table. "Sent" only
 * means Expo accepted the message; this is whether FCM/APNs then reached a
 * phone, which arrives about fifteen minutes later and is the only place a
 * dead token or a credentials fault shows up.
 *
 * @param {object} campaign
 * @param {number} [now]
 * @returns {{ text: string, tone: "muted"|"green"|"red"|"gold", codes: string }}
 */
export function campaignDelivery(campaign, now = Date.now()) {
  const { sent } = campaignCounts(campaign);
  const d = campaign?.delivery || {};
  const delivered = Number(d.delivered || 0);
  const failed = Number(d.failed || 0);
  const codes = Object.entries(d.errorCodes || {})
    .map(([code, n]) => `${code} x${n}`)
    .join(", ");

  if (campaign?.status !== "completed" || sent === 0) {
    return { text: "", tone: "muted", codes: "" };
  }
  if (!d.checkedAt) {
    const since = now - new Date(campaign?.completedAt || campaign?.launchedAt || now).getTime();
    return since > RECEIPT_GRACE_MS
      ? { text: "no receipts read yet", tone: "gold", codes: "" }
      : { text: "awaiting receipts", tone: "muted", codes: "" };
  }
  const pending = Math.max(0, sent - delivered - failed);
  const parts = [`${delivered} delivered`];
  if (failed) parts.push(`${failed} failed`);
  if (pending) parts.push(`${pending} pending`);
  const tone = delivered === 0 && failed > 0 ? "red" : failed > 0 ? "gold" : "green";
  return { text: parts.join(", "), tone, codes };
}

/** Who sent it, however the row happens to carry that. */
export function campaignSender(campaign) {
  return (
    campaign?.createdByName ||
    campaign?.createdByUserId?.name ||
    campaign?.createdByUserId?.email ||
    ""
  );
}
